

import { useMemo, useState, useEffect } from 'react';
import { Stack, TextInput, Textarea, Button, Text, CloseButton, Group, Badge, Select } from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import { Node } from 'reactflow';
import { useQuery } from '@tanstack/react-query';
import { variablesApi } from '@/services/variables.api';
import { toPickerItems } from '@/features/editor/utils/variables';
import PageEditor from './PageEditor';

interface Props {
  node: Node;
  nodes?: Node[];
  onUpdate: (data: any) => void;
  onClose: () => void;
  onAddNode?: (type: string) => string;
  onDelete?: (nodeId: string) => void;
  onSaveFlow?: () => void;
  flowId?: string;
}

export default function PropertyPanel({ node, nodes, onUpdate, onClose, onAddNode, onDelete, onSaveFlow, flowId }: Props) {
  const [data, setData] = useState(node.data);
  const [pageEditorOpened, setPageEditorOpened] = useState(false);

  const { data: apiVariables = [] } = useQuery({
    queryKey: ['variables', 'flow', flowId],
    queryFn: () => flowId ? variablesApi.getFlowVariables(flowId) : Promise.resolve([]),
    enabled: !!flowId,
  });

  const variableOptions = useMemo(() => {
    const pageTitleById = new Map<string, string>();
    (nodes || []).forEach((n) => {
      if (n.type === 'page') pageTitleById.set(n.id, n.data?.title || n.id);
    });
    return toPickerItems(apiVariables, pageTitleById).map((v) => ({ value: v.id, label: v.label }));
  }, [apiVariables, nodes]);

  // Keep the panel synced with node data without closing an active page edit draft.
  useEffect(() => {
    setData(node.data);
  }, [node.data]);

  useEffect(() => {
    setPageEditorOpened(false);
  }, [node.id]);

  const handleChange = (key: string, value: any) => {
    const updated = { ...data, [key]: value };
    setData(updated);
    onUpdate(updated);
  };

  const handleDelete = () => {
    if (node.type === 'start') {
      alert('Cannot delete the Start node. Every flow must have a Start node.');
      return;
    }

    if (confirm(`Are you sure you want to delete this ${data.type} node?`)) {
      onDelete?.(node.id);
      onClose();
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600}>Properties</Text>
        <CloseButton aria-label="Close" onClick={onClose} />
      </Group>

      <TextInput label="ID" value={data.id || ''} disabled />
      <TextInput label="Type" value={data.type || ''} disabled />

      {data.type === 'Note' && (
        <Textarea
          label="Content"
          value={data.content || ''}
          onChange={(e) => handleChange('content', e.currentTarget.value)}
          rows={4}
        />
      )}

      {data.type === 'Prompt' && (
        <>
          <Textarea
            label="Content"
            value={data.content || ''}
            onChange={(e) => handleChange('content', e.currentTarget.value)}
            rows={4}
          />
          <Select
            label="Variable"
            placeholder="Select a variable"
            data={variableOptions}
            value={data.varId || null}
            onChange={(value) => handleChange('varId', value || '')}
            searchable
            nothingFoundMessage="No variables yet"
          />
        </>
      )}

      {data.type === 'Form' && (
        <>
          <TextInput
            label="Title"
            value={data.title || ''}
            onChange={(e) => handleChange('title', e.currentTarget.value)}
          />
          <Text size="sm">Fields: {data.fields?.length || 0}</Text>
        </>
      )}

      {data.type === 'SetVar' && (
        <>
          <Select
            label="Variable"
            placeholder="Select a variable"
            data={variableOptions}
            value={data.varId || null}
            onChange={(value) => handleChange('varId', value || '')}
            searchable
            nothingFoundMessage="No variables yet"
          />
          <TextInput
            label="Value Template"
            value={data.valueTemplate || ''}
            onChange={(e) => handleChange('valueTemplate', e.currentTarget.value)}
          />
        </>
      )}

      {data.type === 'Action' && (
        <>
          <Text size="sm">Action Type: {data.action?.type}</Text>
          <TextInput
            label="Dedupe Key Template"
            value={data.action?.dedupeKeyTemplate || ''}
            onChange={(e) =>
              handleChange('action', { ...data.action, dedupeKeyTemplate: e.currentTarget.value })
            }
          />
        </>
      )}

      {data.type === 'Branch' && (
        <>
          <TextInput
            label="Title"
            value={data.title || ''}
            onChange={(e) => handleChange('title', e.currentTarget.value)}
            placeholder="Choose an option"
          />
          <Textarea
            label="Description"
            value={data.description || ''}
            onChange={(e) => handleChange('description', e.currentTarget.value)}
            rows={2}
            placeholder="Optional description"
          />
          <Text size="sm" fw={500}>Branches:</Text>
          <Stack gap="xs">
            {(data.branches || []).map((branch: any, index: number) => (
              <Group key={index} gap="xs">
                <TextInput
                  placeholder={`Branch ${index + 1} label`}
                  value={branch.label || ''}
                  onChange={(e) => {
                    const updated = [...(data.branches || [])];
                    updated[index] = { ...updated[index], label: e.currentTarget.value };
                    handleChange('branches', updated);
                  }}
                  style={{ flex: 1 }}
                />
                <Button
                  size="xs"
                  color="red"
                  onClick={() => {
                    const updated = (data.branches || []).filter((_: any, i: number) => i !== index);
                    handleChange('branches', updated);
                  }}
                >
                  Remove
                </Button>
              </Group>
            ))}
          </Stack>
          <Button
            size="xs"
            variant="light"
            onClick={() => {
              const updated = [...(data.branches || []), { label: `Option ${(data.branches?.length || 0) + 1}`, target: '' }];
              handleChange('branches', updated);
            }}
            fullWidth
          >
            Add Branch
          </Button>
        </>
      )}

      {data.type === 'Page' && (
        <>
          <TextInput
            label="Title"
            value={data.title || ''}
            onChange={(e) => handleChange('title', e.currentTarget.value)}
          />

          <Group gap="xs" align="center">
            <Text size="sm">Page Elements:</Text>
            <Badge size="sm" variant="light">
              {data.elements?.length || 0}
            </Badge>
          </Group>

          <Button
            leftSection={<IconEdit size={16} />}
            onClick={() => setPageEditorOpened(true)}
            fullWidth
          >
            Open Page Editor
          </Button>
        </>
      )}

      {onDelete && (
        <Button
          onClick={handleDelete}
          fullWidth
          color="red"
          variant="light"
          disabled={node.type === 'start'}
        >
          Delete Node
        </Button>
      )}

      {/* Page Editor Modal */}
      {data.type === 'Page' && (
        <PageEditor
          opened={pageEditorOpened}
          onClose={() => setPageEditorOpened(false)}
          pageNode={data}
          nodes={nodes}
          onAddNode={onAddNode}
          onSave={(updatedNode) => {
            setData(updatedNode);
            onUpdate(updatedNode);
          }}
          onSaveFlow={onSaveFlow}
          flowId={flowId}
        />
      )}
    </Stack>
  );
}
